'use client';

import { useState, useEffect, useCallback } from 'react';

export interface UserSettings {
  audioDeviceId: string;
  videoDeviceId: string;
  audioOutputDeviceId: string;
  audioEnabled: boolean;
  videoEnabled: boolean;
  backgroundType: 'none' | 'blur' | 'image';
  virtualBackgroundImagePath: string;
  noiseSuppressionEnabled: boolean;
}

const DEFAULT_SETTINGS: UserSettings = {
  audioDeviceId: '',
  videoDeviceId: '',
  audioOutputDeviceId: 'default',
  audioEnabled: true,
  videoEnabled: false,
  backgroundType: 'none',
  virtualBackgroundImagePath: '',
  noiseSuppressionEnabled: true,
};

const SETTINGS_KEY = 'evolutto-user-settings';

/** Fundos preset Unsplash removidos do repositório (eram Git LFS inválido no build). */
function sanitizeVirtualBackgroundPath(path: string): string {
  if (path.includes('/background-images/')) {
    return '';
  }
  return path;
}

export function useSettingsPersistence() {
  const [settings, setSettings] = useState<UserSettings>(DEFAULT_SETTINGS);
  const [isLoaded, setIsLoaded] = useState(false);

  const extractSettings = useCallback((raw: unknown): Partial<UserSettings> => {
    if (!raw || typeof raw !== 'object') {
      return {};
    }

    const record = raw as Record<string, unknown>;
    const next: Partial<UserSettings> = {};

    if (typeof record.audioDeviceId === 'string') {
      next.audioDeviceId = record.audioDeviceId;
    }
    if (typeof record.videoDeviceId === 'string') {
      next.videoDeviceId = record.videoDeviceId;
    }
    if (typeof record.audioOutputDeviceId === 'string') {
      next.audioOutputDeviceId = record.audioOutputDeviceId;
    }
    if (typeof record.audioEnabled === 'boolean') {
      next.audioEnabled = record.audioEnabled;
    }
    if (typeof record.videoEnabled === 'boolean') {
      next.videoEnabled = record.videoEnabled;
    }
    if (
      record.backgroundType === 'none' ||
      record.backgroundType === 'blur' ||
      record.backgroundType === 'image'
    ) {
      next.backgroundType = record.backgroundType;
    }
    if (typeof record.virtualBackgroundImagePath === 'string') {
      next.virtualBackgroundImagePath = sanitizeVirtualBackgroundPath(
        record.virtualBackgroundImagePath,
      );
      if (
        next.virtualBackgroundImagePath === '' &&
        record.virtualBackgroundImagePath.includes('/background-images/')
      ) {
        next.backgroundType = 'none';
      }
    } else if (record.virtualBackgroundImagePath === null) {
      next.virtualBackgroundImagePath = '';
    }
    if (typeof record.noiseSuppressionEnabled === 'boolean') {
      next.noiseSuppressionEnabled = record.noiseSuppressionEnabled;
    }

    return next;
  }, []);

  // Carregar configurações do localStorage na inicialização
  useEffect(() => {
    try {
      const savedSettings = localStorage.getItem(SETTINGS_KEY);
      if (savedSettings) {
        const parsedSettings = JSON.parse(savedSettings);
        setSettings({ ...DEFAULT_SETTINGS, ...extractSettings(parsedSettings) });
      }
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    } finally {
      setIsLoaded(true);
    }
  }, []);

  // Salvar configurações no localStorage
  const saveSettings = useCallback(
    (newSettings: Partial<UserSettings>) => {
      const updatedSettings = { ...settings, ...newSettings };

      setSettings(updatedSettings);

      try {
        const settingsJson = JSON.stringify(updatedSettings);
        localStorage.setItem(SETTINGS_KEY, settingsJson);
        window.dispatchEvent(
          new CustomEvent('settings-saved', {
            detail: { ...updatedSettings, __source: 'persistence' },
          }),
        );
      } catch (error) {
        console.error('Erro ao salvar configurações:', error);
      }
    },
    [settings],
  );

  useEffect(() => {
    const handleExternalUpdate = (incoming: Partial<UserSettings>) => {
      setSettings((prev) => ({ ...prev, ...incoming }));
    };

    const handleSettingsSaved = (event: Event) => {
      const customEvent = event as CustomEvent<any>;
      if (!customEvent.detail) return;
      const extracted = extractSettings(customEvent.detail);
      if (Object.keys(extracted).length > 0) {
        handleExternalUpdate(extracted);
      }
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== SETTINGS_KEY || !event.newValue) {
        return;
      }
      try {
        const parsed = JSON.parse(event.newValue);
        const extracted = extractSettings(parsed);
        if (Object.keys(extracted).length > 0) {
          handleExternalUpdate(extracted);
        }
      } catch (error) {
        console.error('Erro ao sincronizar configurações (storage):', error);
      }
    };

    window.addEventListener('settings-saved', handleSettingsSaved as EventListener);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('settings-saved', handleSettingsSaved as EventListener);
      window.removeEventListener('storage', handleStorage);
    };
  }, [extractSettings]);

  // Resetar configurações para padrão
  const resetSettings = () => {
    setSettings(DEFAULT_SETTINGS);
    try {
      localStorage.removeItem(SETTINGS_KEY);
    } catch (error) {
      console.error('Erro ao resetar configurações:', error);
    }
  };

  return {
    settings,
    isLoaded,
    saveSettings,
    resetSettings,
  };
}
